// Orphan / isolation detection for an Atlas — pure functions.
//
// Extracted from atlasLoader.validateAtlas so it can be:
//   1. Called standalone (e.g. CLI `--orphans-only` mode)
//   2. Memoised separately from the rest of validation
//   3. Extended with the cluster-isolation detector below
//
// Four categories (priority order):
//   - 'document-isolated':       no inbound AND no effective outbound refs
//   - 'document-not-referenced': no inbound, has outbound
//   - 'document-does-not-reference': has inbound, no outbound
//   - 'cluster-isolated':        connected component has no edges to other components
//
// The cluster check catches "orphan islands" — e.g. a 3-doc cluster
// that links among itself but never reaches the main graph. The
// per-doc check would happily report all three as healthy because
// they each have inbound + outbound refs.

import type {
  Atlas,
  AtlasDocument,
  AtlasValidationWarning,
  CrossDocumentRef,
} from '@/types/atlas';

export interface OrphanReport {
  isolated: AtlasDocument[];
  notReferenced: AtlasDocument[];
  doesNotReference: AtlasDocument[];
  /** Connected components that contain no edges to any other component. */
  isolatedClusters: AtlasDocument[][];
  warnings: AtlasValidationWarning[];
}

export interface DetectOrphansOptions {
  /** When true, also emit per-cluster warnings. Default true. */
  detectClusters?: boolean;
  /** Skip emitting per-doc isolated/not-referenced/does-not-reference warnings. */
  suppressIsolatedWarnings?: boolean;
}

export function detectOrphans(
  atlas: Atlas,
  opts: DetectOrphansOptions = {},
): OrphanReport {
  const detectClusters = opts.detectClusters !== false;
  const suppressIsolated =
    opts.suppressIsolatedWarnings ?? atlas.settings?.suppressIsolatedWarnings === true;

  const report: OrphanReport = {
    isolated: [],
    notReferenced: [],
    doesNotReference: [],
    isolatedClusters: [],
    warnings: [],
  };

  // ── Per-document inbound/outbound counts ──────────────────────────
  const referencedBy = new Set<string>();
  const referencesOut = new Set<string>();
  for (const ref of atlas.crossDocumentRefs) {
    referencedBy.add(ref.toDocumentId);
    referencesOut.add(ref.fromDocumentId);
    if (ref.bidirectional) {
      referencedBy.add(ref.fromDocumentId);
      referencesOut.add(ref.toDocumentId);
    }
  }

  for (let i = 0; i < atlas.documents.length; i++) {
    const doc = atlas.documents[i];
    const hasInbound = referencedBy.has(doc.id);
    const hasOutbound = referencesOut.has(doc.id);
    const curatedOutbound = (doc.related?.length ?? 0) > 0;
    const effectiveOutbound = hasOutbound || curatedOutbound;
    const path = `/documents/${i}`;

    if (!hasInbound && !effectiveOutbound) {
      report.isolated.push(doc);
      if (!suppressIsolated) {
        report.warnings.push({
          code: 'document-isolated',
          message: `Document "${doc.title}" (${doc.id}) is isolated — no inbound or outbound cross-document references.`,
          path,
        });
      }
    } else if (!hasInbound) {
      report.notReferenced.push(doc);
      if (!suppressIsolated) {
        report.warnings.push({
          code: 'document-not-referenced',
          message: `Document "${doc.title}" (${doc.id}) is not referenced by any other document (it does reference others).`,
          path,
        });
      }
    } else if (!effectiveOutbound) {
      report.doesNotReference.push(doc);
      if (!suppressIsolated) {
        report.warnings.push({
          code: 'document-does-not-reference',
          message: `Document "${doc.title}" (${doc.id}) does not reference any other document (it is referenced by others).`,
          path,
        });
      }
    }
  }

  // ── Cluster detection (connected components) ──────────────────────
  if (detectClusters) {
    const clusters = findConnectedComponents(atlas.documents, atlas.crossDocumentRefs);
    if (clusters.length > 1) {
      // The largest cluster is treated as the "main" graph — flag the
      // rest. (If all clusters are size 1, they're already covered by
      // 'document-isolated' so we skip those.)
      const sorted = [...clusters].sort((a, b) => b.length - a.length);
      const mainSize = sorted[0].length;
      for (const cluster of sorted.slice(1)) {
        if (cluster.length < 2) continue; // singleton orphans already reported
        const docs = cluster
          .map((id) => atlas.documents.find((d) => d.id === id))
          .filter((d): d is AtlasDocument => d != null);
        if (docs.length === 0) continue;
        report.isolatedClusters.push(docs);
        if (!suppressIsolated) {
          report.warnings.push({
            code: 'document-isolated', // reuse existing warning code for UI consistency
            message:
              `Isolated cluster of ${docs.length} documents — they link among themselves ` +
              `but never reach the main graph (${mainSize} docs). ` +
              `Members: ${docs.map((d) => d.title).join(', ')}.`,
            path: `/documents`,
          });
        }
      }
    }
  }

  return report;
}

// ────────────────────────────────────────────────────────────────────
//  Union-Find connected-components — O(n + m·α(n))
// ────────────────────────────────────────────────────────────────────

function findConnectedComponents(
  documents: readonly AtlasDocument[],
  refs: readonly CrossDocumentRef[],
): string[][] {
  // Build a position map docId → index, then a parent array.
  const indexOf = new Map<string, number>();
  documents.forEach((d, i) => indexOf.set(d.id, i));

  const parent = new Array(documents.length);
  for (let i = 0; i < documents.length; i++) parent[i] = i;

  function find(x: number): number {
    let root = x;
    while (parent[root] !== root) root = parent[root];
    // Path-compression.
    while (parent[x] !== root) {
      const next = parent[x];
      parent[x] = root;
      x = next;
    }
    return root;
  }

  function union(a: number, b: number): void {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[ra] = rb;
  }

  for (const ref of refs) {
    const a = indexOf.get(ref.fromDocumentId);
    const b = indexOf.get(ref.toDocumentId);
    if (a == null || b == null) continue;
    union(a, b);
  }

  const groups = new Map<number, string[]>();
  for (let i = 0; i < documents.length; i++) {
    const root = find(i);
    if (!groups.has(root)) groups.set(root, []);
    groups.get(root)!.push(documents[i].id);
  }
  return Array.from(groups.values());
}

// ────────────────────────────────────────────────────────────────────
//  Memoised wrapper — keyed on identity of refs+docs+settings
// ────────────────────────────────────────────────────────────────────

const memoCache = new WeakMap<Atlas, { settingsKey: string; report: OrphanReport }>();

/**
 * Memoised orphan detection. Recomputes only when the atlas object
 * reference changes OR the settings sub-key relevant to orphan
 * detection changes. Safe to call on every render.
 */
export function detectOrphansMemoised(
  atlas: Atlas,
  opts: DetectOrphansOptions = {},
): OrphanReport {
  const settingsKey = JSON.stringify({
    detectClusters: opts.detectClusters !== false,
    suppress:
      opts.suppressIsolatedWarnings ?? atlas.settings?.suppressIsolatedWarnings === true,
    refLen: atlas.crossDocumentRefs.length,
    docLen: atlas.documents.length,
  });
  const cached = memoCache.get(atlas);
  if (cached && cached.settingsKey === settingsKey) return cached.report;
  const report = detectOrphans(atlas, opts);
  memoCache.set(atlas, { settingsKey, report });
  return report;
}
